import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToSection } from "../lib/scroll";

const LINKS = [
    { label: "Home", hash: "#home" },
    { label: "About", hash: "#about" },
    { label: "Solutions", hash: "#domains" },
    { label: "IT", route: "/solutions/it-system-integration" },
    { label: "Impact", hash: "#impact" },
    { label: "Contact", hash: "#contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (item) => {
        setOpen(false);
        const run = () => {
            if (item.route) {
                navigate(item.route);
            } else if (pathname !== "/") {
                navigate("/", { state: { scrollTo: item.hash } });
            } else {
                scrollToSection(item.hash);
            }
        };
        setTimeout(run, open ? 350 : 0);
    };

    return (
        <>
            <motion.header
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                    scrolled || pathname !== "/"
                        ? "border-b border-[#DCE5D4] bg-white/85 shadow-[0_10px_40px_rgba(14,17,18,0.08)] backdrop-blur-xl"
                        : "border-b border-[#DCE5D4]/60 bg-white/70 backdrop-blur-md"
                }`}
                data-testid="main-nav"
            >
                <nav className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 md:px-10">
                    <button
                        onClick={() => go({ hash: "#home" })}
                        className="flex items-center gap-3"
                        data-testid="nav-logo"
                        aria-label="NOMAS home"
                    >
                        <img
                            src="/assets/nomas-logo-light.png"
                            alt="NOMAS"
                            className="h-7 w-auto md:h-8"
                        />
                    </button>

                    <div className="hidden items-center gap-8 lg:flex">
                        {LINKS.map((item) => (
                            <button
                                key={item.label}
                                onClick={() => go(item)}
                                data-testid={`nav-link-${item.label.toLowerCase()}`}
                                className="group relative text-[13px] font-medium tracking-[0.14em] text-[#45543F] uppercase transition-colors duration-300 hover:text-[#0E1112]"
                            >
                                {item.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#598B3B] transition-[width] duration-400 group-hover:w-full" />
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => go({ hash: "#contact" })}
                            data-testid="nav-cta-talk"
                            className="hidden rounded-full border border-[#598B3B]/60 px-6 py-2.5 text-[13px] font-semibold tracking-[0.12em] text-[#0E1112] uppercase transition-[background-color,color,border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-[#598B3B] hover:bg-[#598B3B] hover:text-white lg:block"
                        >
                            Let&rsquo;s Talk
                        </button>
                        <button
                            onClick={() => setOpen(!open)}
                            data-testid="nav-hamburger"
                            aria-label="Toggle menu"
                            className="relative flex h-10 w-10 flex-col items-center justify-center gap-[7px] lg:hidden"
                        >
                            <span
                                className={`h-px w-6 bg-[#0E1112] transition-[transform,opacity] duration-400 ${open ? "translate-y-[4px] rotate-45" : ""}`}
                            />
                            <span
                                className={`h-px w-6 bg-[#0E1112] transition-[transform,opacity] duration-400 ${open ? "-translate-y-[4px] -rotate-45" : ""}`}
                            />
                        </button>
                    </div>
                </nav>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35 }}
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-white/97 px-8 backdrop-blur-2xl lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex flex-col gap-2">
                            {LINKS.map((item, i) => (
                                <div key={item.label} className="overflow-hidden">
                                    <motion.button
                                        initial={{ y: "110%" }}
                                        animate={{ y: 0 }}
                                        exit={{ y: "110%" }}
                                        transition={{
                                            duration: 0.6,
                                            delay: 0.06 * i,
                                            ease: [0.16, 1, 0.3, 1],
                                        }}
                                        onClick={() => go(item)}
                                        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                        className="font-display py-2 text-left text-4xl font-light tracking-tight text-[#0E1112] transition-colors duration-300 hover:text-[#598B3B]"
                                    >
                                        <span className="mr-4 text-sm text-[#598B3B]">
                                            0{i + 1}
                                        </span>
                                        {item.label}
                                    </motion.button>
                                </div>
                            ))}
                        </div>
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            onClick={() => go({ hash: "#contact" })}
                            data-testid="mobile-nav-cta"
                            className="mt-12 w-max rounded-full bg-[#598B3B] px-8 py-3.5 text-sm font-semibold tracking-[0.12em] uppercase text-white transition-[background-color] duration-300 hover:bg-[#4a7530]"
                        >
                            Let&rsquo;s Talk
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
